/**
 * Page-level breadcrumb overrides for the DashboardLayout top-bar.
 *
 * The layout derives breadcrumbs from the URL — which is fine for every
 * list route but misses context for detail pages. A deep link like
 * `/admin/invoices/INV-2815` otherwise shows the internal Mongo id as the
 * last crumb; we'd much rather show the friendlier Xero invoice number
 * (`IN202600025`) so the user sees one consistent reference everywhere.
 *
 * Usage:
 *   const override = useBreadcrumbTail();
 *   useEffect(() => override.set('IN202600025'), [xeroNumber]);
 * or the shorthand effect wrapper:
 *   useBreadcrumbOverride(xeroNumber);   // auto-clears on unmount
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const BreadcrumbCtx = createContext({
  tail: null,
  set: () => {},
  clear: () => {},
});

export const BreadcrumbOverrideProvider = ({ children }) => {
  const [tail, setTail] = useState(null);
  const set = useCallback((label) => setTail(label || null), []);
  const clear = useCallback(() => setTail(null), []);
  const value = useMemo(() => ({ tail, set, clear }), [tail, set, clear]);
  return <BreadcrumbCtx.Provider value={value}>{children}</BreadcrumbCtx.Provider>;
};

export const useBreadcrumbTailValue = () => useContext(BreadcrumbCtx).tail;

export const useBreadcrumbOverride = (label) => {
  const { set, clear } = useContext(BreadcrumbCtx);
  useEffect(() => {
    if (label) set(label);
    return clear;
  }, [label, set, clear]);
};
